import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Select, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import ScreenRotationIcon from '@material-ui/icons/ScreenRotation';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ITemplate } from '../../main/entities';
import { transl } from '../../main/transl';
import { useNoSubmenu } from '../../mainpanel/useSubmenu';
import { mg } from '../../mg/mg';
import { toasterSvc } from '../../services/ToasterSvc';
import { useOmnistore } from '../../stores/Omnistore';

const useStyles = makeStyles({
	TeamPage: {
	},
	TeamDialog: {
	},
	table: {
		display: 'grid',
		gap: '2px',
	},
	header: {
		padding: 10,
		width: '100%',
		textAlign: 'center',
		backgroundColor: '#DDD',
	},
	summary: {
		display: 'grid',
		gridTemplateColumns: 'auto 1fr',
		gap: '10px',
		alignItems: 'center',
	},
	bold: {
		fontWeight: 'bold',
	},
	span2: {
		gridColumn: 'span 2',
	},
	hint: {
		color: 'red',
		gridColumn: 'span 2',
	},
});

const CAPABILITIES = ['NONE', 'MANAGE_ITEMS', 'ORDER'];

class TemplateStub {
	id: number;
	ownerEmail: string;

	constructor(template: ITemplate, public readonly name: string) {
		this.id = template.id;
		this.ownerEmail = template.ownerEmail;
	}
}

type IChoices = 'SET' | 'LEAVE' | 'NONE';

class CellData {
	constructor(
		public readonly email: string,
		public readonly template: TemplateStub,
		public readonly ownEmail: string,
		public readonly capability: typeof CAPABILITIES[number],
	) {
		this.choices = template.ownerEmail===ownEmail ? email===ownEmail ? 'NONE' : 'SET' : email===ownEmail && capability!=='NONE' ? 'LEAVE' : 'NONE';
	}

	public readonly choices: IChoices;

	get color() : 'primary' | 'secondary' | 'default' {
		return this.choices==='SET' ? 'primary' : this.choices==='LEAVE' ? 'secondary' : 'default';
	}
}

interface ITeamCellProps {
	cellData: CellData
	setDialogData(cellData: CellData): void
}

const TeamCell = observer(function TeamCell(props: ITeamCellProps) {
	const {cellData, setDialogData} = props;
	function onClick() {
		setDialogData(cellData);
	}
	return <Button color={cellData.color} onClick={onClick} variant="outlined">
		{transl('Capability.' + cellData.capability)}
	</Button>;
});

interface IExternalTeamDialogProps {
	template: ITemplate
	onClose(): void
}

export const ExternalTeamDialog = observer(function ExternalTeamDialog(props: IExternalTeamDialogProps) {
	const {template, onClose} = props;
	const omnistore = useOmnistore();
	const supplier = omnistore.multirepo.supplierRepo.byId(template.supplierId);
	const t = new TemplateStub(template, supplier?.name || '');
	const cellData = new CellData('', t, omnistore.profileStore.ownUserEmail, 'NONE');
	return <TeamDialog cellData={cellData} onClose={onClose}/>;
});

interface ITeamDialogProps {
	cellData: CellData
	onClose(): void
}

const TeamDialog = observer(function TeamDialog(props: ITeamDialogProps) {
	const classes = useStyles();
	const omnistore = useOmnistore();
	const branch = omnistore.multirepo.branchRepo.byId(omnistore.locationStore.branchId);
	const {cellData, onClose} = props;
	const [capability, setCapability] = React.useState(cellData.capability);
	const [email, setEmail] = React.useState(cellData.email);
	const [invite, setInvite] = React.useState(false);
	React.useEffect(() => setInvite(false), [email]);
	console.log(JSON.stringify(cellData));
	const hasChanged = capability !== cellData.capability;
	function save() {
		omnistore.communicator.doPost('GrantTemplate', {capability, entityId: cellData.template.id, invite, userIdOrEmail: email})
			.then(toasterSvc.handleResponse('team.accessSet'))
			.then(r => {
				if (r.outputList) {
					omnistore.multirepo.accessTemplateRepo.fetch('DIRECT');
					onClose();
				} else if (!invite && r.failure?.type==='NOT_FOUND' && r.failure.subject==='USER_ID_OR_EMAIL') {
					setInvite(true);
				}
			});
	}
	return <Dialog fullWidth open={true} onClose={() => !hasChanged && onClose()}>
		<DialogTitle>{transl(!invite ? 'team.grantTemplate' : 'team.grantTemplateAndInvite')}</DialogTitle>
		<DialogContent>
			<div className={classes.summary}>
				<div className={classes.bold}>{transl('branch')}</div>
				<div>{branch?.name}</div>
				<div className={classes.bold}>{transl('supplier')}</div>
				<div>{cellData.template.name}</div>
				<div className={classes.bold}>{transl('team.email')}</div>
				{
					cellData.email || cellData.choices==='NONE' ? <div>{email}</div> : <TextField
						required
						type="email"
						label={transl('team.newUserEmail')}
						value={email}
						onChange={e => setEmail(e.target.value)}
					/>
				}
				<div className={classes.bold}>{transl('team.currentAccess')}</div>
				<div>{transl('Capability.' + cellData.capability)}</div>
				<div className={classes.span2}>&nbsp;</div>
				{cellData.choices!=='SET' ? null : <>
					<div className={classes.bold}>{transl('team.newAccess')}</div>
					<Select value={capability} onChange={(e: any) => setCapability(e.target.value)}>
						{CAPABILITIES.map(c => <MenuItem key={c} value={c}>{transl('Capability.' + c)}</MenuItem>)}
					</Select>
				</>}
				{cellData.choices!=='LEAVE' ? null : <Button
					onClick={() => setCapability('NONE')}
					disabled={hasChanged}
					variant="contained"
					color="secondary"
					className={classes.span2}
				>
					{transl('team.giveUpAccess')}
				</Button>}
				{cellData.choices!=='NONE' ? null : <div className={classes.hint}>
					{transl(cellData.template.ownerEmail===cellData.ownEmail ? 'team.youAreTheOwner' : 'team.youAreNotTheOwner')}
				</div>}
				{!invite ? null : <div className={classes.hint}>{transl('team.invitation')}</div>}
			</div>
		</DialogContent>
		<DialogActions>
			<Button onClick={onClose}>{transl('cancel')}</Button>
			<Button color={cellData.choices==='LEAVE' ? 'secondary' : 'primary'} disabled={!hasChanged || !email} onClick={save}>
				{transl(cellData.choices!=='LEAVE' ? 'save' : 'team.reallyGiveUpAccess')}
			</Button>
		</DialogActions>
	</Dialog>;
});

export const TeamPage = observer(function Team() {
	const classes = useStyles();
	const omnistore = useOmnistore();
	useNoSubmenu();
	const [transposed, setTransposed] = React.useState(false);
	const [dialogData, setDialogData] = React.useState<CellData|null>(null);

	const locationStore = omnistore.locationStore;
	const multirepo = omnistore.multirepo;
	const ownEmail = omnistore.profileStore.ownUserEmail;
	const templates = multirepo.templateRepo.allAsList.filter(t => t.branchId === locationStore.branchId)
		.map(t => new TemplateStub(t, multirepo.supplierRepo.byId(t.supplierId)?.name ?? '???'))
		.sort(mg.comparatorOn(t => t.name));
	const templateIds = new Set(templates.map(t => t.id));
	const accessTemplates = multirepo.accessTemplateRepo.allAsList.filter(at => templateIds.has(at.templateId));
	const emails = [...new Set(accessTemplates.map(at => at.email))].sort();
	const capability = (email: string, template: TemplateStub) : string => accessTemplates
		.find(at => at.email===email && at.templateId===template.id)?.capability ?? 'NONE';

	const tableStyle = transposed ? {
		gridTemplateColumns: `auto repeat(${templates.length}, 1fr)`,
		gridTemplateRows: `auto repeat(${emails.length}, auto) auto`,
		gridAutoFlow: 'column dense',
	} : {
		gridTemplateColumns: `auto repeat(${emails.length}, 1fr) auto`,
		gridTemplateRows: `auto repeat(${templates.length}, auto)`,
		gridAutoFlow:'row dense',
	};

	React.useEffect(() => {
		multirepo.branchRepo.fetch();
		multirepo.supplierRepo.fetch();
		multirepo.templateRepo.fetch();
		multirepo.accessTemplateRepo.fetch();
	}, []);

	return <div className={classes.TeamPage}>
		<div className={classes.table} style={tableStyle}>
			<div className={classes.header}>
				<IconButton onClick={() => setTransposed(!transposed)}><ScreenRotationIcon/></IconButton>
			</div>
			{emails.map(e => <div key={e} className={classes.header}>{e}</div>)}
			<div className={classes.header}>{transl('team.add')}</div>

			{templates.map(t => <React.Fragment key={t.id}>
				<div className={classes.header}>{t.name}</div>
				{emails.map(e => <TeamCell key={e} cellData={new CellData(e, t, ownEmail, capability(e, t))} setDialogData={setDialogData}/>)}
				<IconButton onClick={() => setDialogData(new CellData('', t, ownEmail, 'NONE'))}><AddIcon/></IconButton>
			</React.Fragment>)}
		</div>
		{!dialogData ? null : <TeamDialog cellData={dialogData} onClose={() => setDialogData(null)}/>}
	</div>;
});
